import { AJUDA_ARQUIVOS } from "@/lib/ajuda/dialogos/arquivos";
import { useEffect, useState } from "react";
import { ChevronRight, Folder } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";

export type MoveTarget = { id: string; name: string; kind: "arquivo" | "pasta" } | null;

/** Mini navegador de pastas — usuário entra em subpastas até achar o destino, depois confirma
 *  "Mover para cá". Ciclo (mover pasta pra dentro dela mesma/descendente) é bloqueado no backend;
 *  aqui só mostra o erro se acontecer. */
export function MoveDialog({
  target,
  onClose,
  onMove,
  isPending,
}: {
  target: MoveTarget;
  onClose: () => void;
  onMove: (folderId: string | null) => void;
  isPending: boolean;
}) {
  const [navFolderId, setNavFolderId] = useState<string | null>(null);

  useEffect(() => {
    if (target) setNavFolderId(null);
  }, [target]);

  const listing = trpc.file.listFolder.useQuery({ folderId: navFolderId }, { enabled: !!target });

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md dark:bg-card">
        <DialogHeader>
          <DialogTitle ajuda={AJUDA_ARQUIVOS.mover}>Mover {target?.kind}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-1 text-xs text-zinc-500 dark:text-muted-foreground">
            <button type="button" onClick={() => setNavFolderId(null)} className="hover:text-brand-700 hover:underline dark:hover:text-primary">
              Raiz
            </button>
            {listing.data?.breadcrumb.map((b) => (
              <span key={b.id} className="flex items-center gap-1">
                <ChevronRight className="h-3 w-3" />
                <button
                  type="button"
                  onClick={() => setNavFolderId(b.id)}
                  className="hover:text-brand-700 hover:underline dark:hover:text-primary"
                >
                  {b.name}
                </button>
              </span>
            ))}
          </div>

          <div className="max-h-64 overflow-y-auto rounded-md border border-zinc-200 dark:border-border">
            {!listing.data || listing.data.subfolders.length === 0 ? (
              <p className="p-4 text-center text-xs text-zinc-400 dark:text-muted-foreground">
                Nenhuma subpasta aqui
              </p>
            ) : (
              listing.data.subfolders.map((f: { id: string; name: string }) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setNavFolderId(f.id)}
                  className="flex w-full items-center gap-2 border-b border-zinc-100 px-3 py-2 text-left text-sm last:border-b-0 hover:bg-zinc-50 dark:border-border/50 dark:hover:bg-muted"
                >
                  <Folder className="h-4 w-4 shrink-0 text-brand-500 dark:text-primary" />
                  {f.name}
                </button>
              ))
            )}
          </div>
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 items-center rounded-md border border-zinc-200 px-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50 dark:border-border dark:text-foreground dark:hover:bg-muted"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={isPending}
            onClick={() => onMove(navFolderId)}
            className="inline-flex h-9 items-center rounded-md bg-brand-600 px-3 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
          >
            Mover pra cá
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
